import React, { useState, useEffect, useMemo } from 'react';
import {
  Tabs,
  Button,
  Form,
  Input,
  Tree,
  DatePicker,
  Radio,
  Space,
  Card,
  Table,
  Progress,
  Tag,
  Select,
  Row,
  Col,
  message,
  Modal,
  List,
  Avatar,
  Divider,
  Spin,
  Popconfirm,
  App,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  SaveOutlined,
  SendOutlined,
  TeamOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import type { ColumnsType } from 'antd/es/table';
import { NoticeService, type NoticeWithRelations } from '../services/noticeService';
import { UserService } from '../services/userService';
import { supabase } from '../services/supabase';
import type { Database } from '../types/database.types';
import dayjs from 'dayjs';
import './NoticeManagement.css';
import { logger } from '../utils/logger';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

type User = Database['public']['Tables']['users']['Row'];

interface OrganizationNode extends DataNode {
  userCount?: number;
  type?: 'company' | 'branch' | 'all';
  companyId?: string;
  branchId?: string;
}

const NoticeManagement: React.FC = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [selectedTargets, setSelectedTargets] = useState<React.Key[]>([]);
  const [selectedUserCount, setSelectedUserCount] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<NoticeWithRelations | null>(null);
  const [notices, setNotices] = useState<NoticeWithRelations[]>([]);
  const [organizationTree, setOrganizationTree] = useState<OrganizationNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [sendingNotice, setSendingNotice] = useState(false);
  const [editingNotice, setEditingNotice] = useState<NoticeWithRelations | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [activeTab, setActiveTab] = useState('history');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [hasLoadedStats, setHasLoadedStats] = useState(false);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadInitialData();
  }, []);

  // 탭 변경 시 필요한 데이터 로드
  useEffect(() => {
    if (activeTab === 'create' && organizationTree.length === 0) {
      loadOrganizationTree();
    }
  }, [activeTab]);

  // 실시간 구독 설정 - currentUser가 있을 때만
  useEffect(() => {
    if (!currentUser) return;

    const noticesSubscription = supabase
      .channel('notices-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'tbm_messages' 
        }, 
        () => {
          // 공지사항 변경 시 목록 새로고침 (통계 포함 여부는 현재 상태 유지)
          loadNotices(currentUser, hasLoadedStats);
        }
      )
      .subscribe();

    const confirmationsSubscription = supabase
      .channel('confirmations-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'tbm_confirmations' 
        }, 
        () => {
          // 확인 상태 변경 시 목록 새로고침 (통계 업데이트 필요)
          loadNotices(currentUser, true);
          setHasLoadedStats(true);
        }
      )
      .subscribe();

    return () => {
      noticesSubscription.unsubscribe();
      confirmationsSubscription.unsubscribe();
    };
  }, [currentUser]);

  // 필터 변경 시 자동 검색 (통계 포함 여부 결정)
  useEffect(() => {
    if (currentUser) {
      // 이미 통계를 로드한 경우에만 통계 포함
      loadNotices(currentUser, hasLoadedStats);
    }
  }, [filterStatus, filterDateRange]);

  // 초기 데이터 로드
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 현재 사용자 정보 가져오기
      const { data: { user } } = await supabase.auth.getUser();
      let userData: User | null = null;
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', user.id)
          .single();
        userData = data;
        setCurrentUser(userData);
      }

      // 초기 로딩 시 통계와 함께 로드
      await loadNotices(userData, true);
      setHasLoadedStats(true);
    } catch (error) {
      logger.error('Error loading initial data:', error);
      message.error('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 공지사항 목록 로드
  const loadNotices = async (user?: User | null, includeStats: boolean = false) => {
    setTableLoading(true);
    try {
      const filters: any = {};
      
      logger.debug('loadNotices 호출됨', { filterStatus, user: user || currentUser });
      
      // 검색어 필터
      if (searchTerm) {
        filters.searchTerm = searchTerm;
      }
      
      // 상태 필터
      if (filterStatus !== 'all') {
        if (filterStatus === 'sent') {
          filters.isSent = true;
        } else if (filterStatus === 'scheduled') {
          filters.isSent = false;
          // scheduled_at이 null이 아닌 것만
        }
      }
      
      // 날짜 범위 필터
      if (filterDateRange && filterDateRange[0] && filterDateRange[1]) {
        filters.startDate = filterDateRange[0].startOf('day').toISOString();
        filters.endDate = filterDateRange[1].endOf('day').toISOString();
      }
      
      logger.debug('NoticeService.getNotices 호출 전', { filters });
      const data = await NoticeService.getNotices(filters, user || currentUser, includeStats);
      logger.debug('NoticeService.getNotices 결과', { count: data.length });
      
      // 추가 필터링 (scheduled)
      let filteredData = data;
      if (filterStatus === 'scheduled') {
        filteredData = data.filter(n => n.scheduled_at && !n.is_sent);
        logger.debug('scheduled 필터링 후', { count: filteredData.length });
      }
      
      // 즉시발송 공지만 확인
      const immediateSentNotices = data.filter(n => n.is_sent && !n.scheduled_at);
      logger.debug('즉시발송 공지사항', { count: immediateSentNotices.length, notices: immediateSentNotices.map(n => ({ id: n.id, title: n.title, is_sent: n.is_sent, sent_at: n.sent_at })) });
      
      setNotices(filteredData);
    } catch (error) {
      logger.error('Error loading notices:', error);
      message.error('공지사항 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setTableLoading(false);
    }
  };

  // 조직도 트리 데이터 로드
  const loadOrganizationTree = async () => {
    setTreeLoading(true);
    try {
      // 현재 사용자 정보 확인
      const { data: { user } } = await supabase.auth.getUser();
      let userData: User | null = null;
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', user.id)
          .single();
        userData = data;
      }

      // 일반 관리자는 자신의 회사만 로드
      let companyFilter = {};
      if (userData?.has_admin_role && !userData.is_admin && userData.company_id) {
        companyFilter = { company_id: userData.company_id };
      }

      // 필요한 데이터만 선택적으로 로드
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name, company_code')
        .order('name');

      const { data: branches } = await supabase
        .from('branches')
        .select('id, branch_name, branch_code, company_id')
        .order('branch_name');

      // 활성 사용자 수만 집계 쿼리로 가져오기
      const { data: userCounts } = await supabase
        .from('users')
        .select('company_id, branch_id')
        .eq('is_active', true)
        .eq('is_deleted', false)
        .eq('is_admin', false);

      // 사용자 수 계산
      const userCountByCompany: Record<string, number> = {};
      const userCountByBranch: Record<string, number> = {};
      const uniqueUsers = new Set<string>();

      userCounts?.forEach(user => {
        uniqueUsers.add(`${user.company_id}-${user.branch_id}`);
        if (user.company_id) {
          userCountByCompany[user.company_id] = (userCountByCompany[user.company_id] || 0) + 1;
        }
        if (user.branch_id) {
          userCountByBranch[user.branch_id] = (userCountByBranch[user.branch_id] || 0) + 1;
        }
      });

      // 필터링 적용
      let filteredCompanies = companies || [];
      let filteredBranches = branches || [];
      
      if (userData && userData.has_admin_role && !userData.is_admin && userData.company_id) {
        const userCompanyId = userData.company_id;
        filteredCompanies = filteredCompanies.filter(c => c.id === userCompanyId);
        filteredBranches = filteredBranches.filter(b => b.company_id === userCompanyId);
      }

      // 트리 데이터 구성
      const treeData: OrganizationNode[] = [{
        title: `전체 (${uniqueUsers.size}명)`,
        key: 'all',
        type: 'all',
        userCount: uniqueUsers.size,
        children: filteredCompanies.map(company => ({
          title: `${company.name} (${userCountByCompany[company.id] || 0}명)`,
          key: `company-${company.id}`,
          type: 'company',
          companyId: company.id,
          userCount: userCountByCompany[company.id] || 0,
          children: filteredBranches
            .filter(branch => branch.company_id === company.id)
            .map(branch => ({
              title: `${branch.branch_name} (${userCountByBranch[branch.id] || 0}명)`,
              key: `branch-${branch.id}`,
              type: 'branch',
              branchId: branch.id,
              companyId: company.id,
              userCount: userCountByBranch[branch.id] || 0
            }))
        }))
      }];

      setOrganizationTree(treeData);
    } catch (error) {
      logger.error('Error loading organization tree:', error);
      message.error('조직도를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setTreeLoading(false);
    }
  };


  // 공지사항 목록 테이블 컬럼 (메모이제이션)
  const noticeColumns: ColumnsType<NoticeWithRelations> = useMemo(() => [
    {
      title: '구분',
      dataIndex: 'notice_type',
      key: 'notice_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'TBM' ? 'blue' : 'green'}>
          {type === 'TBM' ? 'TBM' : '공지사항'}
        </Tag>
      ),
    },
    {
      title: '제목',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '대상',
      key: 'target',
      width: 150,
      render: (_, record) => {
        // 모바일 앱 기준에 따른 대상 표시
        if (record.target_type === 'branch' && record.branch) {
          return record.branch.branch_name;
        } else if (record.target_type === 'company') {
          if (!record.company_id && !record.branch_id) {
            return '전체';
          } else if (record.company) {
            return `${record.company.name} 전체`;
          }
        }
        return '-';
      },
    },
    {
      title: '발송일시',
      key: 'sentDate',
      width: 160,
      render: (_, record) => {
        if (record.is_sent && record.sent_at) {
          return dayjs(record.sent_at).format('YYYY-MM-DD HH:mm');
        } else if (record.scheduled_at) {
          return (
            <Tag color="blue">
              {dayjs(record.scheduled_at).format('MM/DD HH:mm')} 예약
            </Tag>
          );
        }
        return '-';
      },
    },
    {
      title: '읽음률',
      dataIndex: 'read_rate',
      key: 'read_rate',
      width: 120,
      render: (rate: number, record) => 
        record.is_sent ? (
          <Progress percent={rate || 0} size="small" />
        ) : (
          '-'
        ),
    },
    {
      title: '상태',
      key: 'status',
      width: 100,
      render: (_, record) => {
        if (record.is_sent) {
          return <Tag color="green">발송완료</Tag>;
        } else if (record.scheduled_at) {
          return <Tag color="blue">예약</Tag>;
        }
        return <Tag color="default">대기</Tag>;
      },
    },
    {
      title: '작업',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            상세
          </Button>
          {!record.is_sent && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              수정
            </Button>
          )}
          {!record.is_sent && (
            <Button
              type="link"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleSendNow(record)}
            >
              발송
            </Button>
          )}
          {!record.is_sent ? (
            <Popconfirm
              title="정말 삭제하시겠습니까?"
              onConfirm={() => handleDelete(record.id)}
              okText="삭제"
              cancelText="취소"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                삭제
              </Button>
            </Popconfirm>
          ) : (
            <Tooltip title="발송된 공지사항은 삭제할 수 없습니다" mouseEnterDelay={0.1}>
              <Button 
                type="link" 
                size="small" 
                danger 
                icon={<DeleteOutlined />} 
                disabled
                style={{ cursor: 'not-allowed' }}
              >
                삭제
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ], [currentUser]);

  // 테이블 데이터 소스 (간단하게 notices 사용)
  const tableDataSource = notices;

  // 트리 선택 처리
  const handleTreeSelect = (checkedKeys: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
    const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    setSelectedTargets(keys);
    
    // 선택된 대상의 인원수 계산
    let totalCount = 0;
    const findNode = (nodes: OrganizationNode[], key: React.Key): OrganizationNode | null => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findNode(node.children as OrganizationNode[], key);
          if (found) return found;
        }
      }
      return null;
    };

    keys.forEach(key => {
      const node = findNode(organizationTree, key);
      if (node) {
        totalCount += node.userCount || 0;
      }
    });

    setSelectedUserCount(totalCount);
  };

  // 미리보기
  const handlePreview = () => {
    form.validateFields(['title', 'content']).then((values) => {
      if (!values.title?.trim() || !values.content?.trim()) {
        message.warning('제목과 내용을 입력해주세요.');
        return;
      }
      setPreviewVisible(true);
    }).catch(() => {
      message.warning('제목과 내용을 입력해주세요.');
    });
  };

  // 공지사항 발송
  const handleSend = async () => {
    try {
      const values = await form.validateFields();
      if (selectedTargets.length === 0) {
        message.warning('발송 대상을 선택해주세요.');
        return;
      }

      setSendingNotice(true);
      
      logger.debug('공지사항 발송 시작');

      // 선택된 대상 분석
      let targetType: 'company' | 'branch' = 'branch';
      let companyId: string | undefined;
      let branchId: string | undefined;

      // 선택된 첫 번째 대상으로 타입 결정
      const firstTarget = selectedTargets[0] as string;
      if (firstTarget === 'all') {
        // 전체 공지: 모바일 앱 기준에 따라 company_id와 branch_id 모두 null로 설정
        targetType = 'company';
        companyId = undefined;
        branchId = undefined;
      } else if (firstTarget.startsWith('company-')) {
        targetType = 'company';
        companyId = firstTarget.replace('company-', '');
      } else if (firstTarget.startsWith('branch-')) {
        targetType = 'branch';
        branchId = firstTarget.replace('branch-', '');
        // branch의 company ID 찾기
        const node = findNodeByKey(organizationTree, firstTarget);
        if (node && node.companyId) {
          companyId = node.companyId;
        }
      }

      const noticeData = {
        title: values.title,
        content: values.content,
        targetType,
        companyId,
        branchId,
        targetDate: dayjs().format('YYYY-MM-DD'),
        scheduledAt: values.sendType === 'scheduled' && values.scheduledTime 
          ? values.scheduledTime.toISOString() 
          : undefined,
        noticeType: values.noticeType || 'NOTICE',
      };

      logger.debug('공지사항 생성 데이터', noticeData);
      logger.debug('선택된 대상', { target: firstTarget });

      // 전체 공지는 하나의 공지사항으로 생성 (company_id, branch_id 모두 null)
      if (firstTarget === 'all' && !editingNotice) {
        const allNoticeData = {
          ...noticeData,
          targetType: 'company' as 'company' | 'branch',
          companyId: undefined,
          branchId: undefined,
          noticeType: values.noticeType || 'NOTICE'
        };
        
        const notice = await NoticeService.createNotice(allNoticeData, currentUser?.id ?? '');
        
        // 즉시 발송인 경우
        if (values.sendType === 'immediate') {
          await NoticeService.sendNotice(notice.id);
        }
        
        if (values.sendType === 'immediate') {
          logger.debug('전체 즉시 발송 완료 알림 표시');
          notification.success({
            message: '발송 완료',
            description: '전체 사용자에게 공지사항이 발송되었습니다.',
            placement: 'topRight',
            duration: 4,
          });
          
          // 알림이 표시될 시간을 주기 위해 약간의 지연 추가
          await new Promise(resolve => setTimeout(resolve, 100));
        } else if (values.sendType === 'scheduled') {
          logger.debug('전체 예약 완료 알림 표시');
          notification.success({
            message: '예약 완료',
            description: '전체 사용자에게 공지사항이 예약되었습니다.',
            placement: 'topRight',
            duration: 4,
          });
          
          // 알림이 표시될 시간을 주기 위해 약간의 지연 추가
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } else {
        // 기존 로직
        const notice = editingNotice
          ? await NoticeService.updateNotice(editingNotice.id, noticeData)
          : await NoticeService.createNotice(noticeData, currentUser?.id ?? '');

        // 즉시 발송인 경우
        if (values.sendType === 'immediate') {
          await NoticeService.sendNotice(notice.id);
          logger.debug('개별 공지 즉시 발송 완료');
          notification.success({
            message: '발송 완료',
            description: '공지사항이 발송되었습니다.',
            placement: 'topRight',
            duration: 4,
          });
          
          // 알림이 표시될 시간을 주기 위해 약간의 지연 추가
          await new Promise(resolve => setTimeout(resolve, 100));
        } else if (values.sendType === 'scheduled') {
          logger.debug('개별 공지 예약 완료');
          notification.success({
            message: '예약 완료',
            description: '공지사항이 예약되었습니다.',
            placement: 'topRight',
            duration: 4,
          });
          
          // 알림이 표시될 시간을 주기 위해 약간의 지연 추가
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // 알림이 표시된 후 폼 초기화 및 목록 새로고침
      setTimeout(async () => {
        form.resetFields();
        setEditingNotice(null);
        setSelectedTargets([]);
        setSelectedUserCount(0);
        await loadNotices(currentUser);
        // 발송 이력 탭으로 전환
        setActiveTab('history');
      }, 500);
    } catch (error) {
      logger.error('Error sending notice:', error);
      message.error('공지사항 발송 중 오류가 발생했습니다.');
    } finally {
      setSendingNotice(false);
    }
  };

  // 상세 보기
  const handleViewDetail = async (notice: NoticeWithRelations) => {
    try {
      const detailedNotice = await NoticeService.getNotice(notice.id);
      setSelectedNotice(detailedNotice);
      setDetailModalVisible(true);
    } catch (error) {
      logger.error('Error loading notice detail:', error);
      message.error('공지사항 상세 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 수정
  const handleEdit = (notice: NoticeWithRelations) => {
    setEditingNotice(notice);
    form.setFieldsValue({
      title: notice.title,
      content: notice.content,
      sendType: notice.scheduled_at ? 'scheduled' : 'immediate',
      scheduledTime: notice.scheduled_at ? dayjs(notice.scheduled_at) : undefined,
      noticeType: notice.notice_type || 'NOTICE',
    });

    // 대상 설정
    const targets: React.Key[] = [];
    if (notice.target_type === 'company' && notice.company_id) {
      targets.push(`company-${notice.company_id}`);
    } else if (notice.target_type === 'branch' && notice.branch_id) {
      targets.push(`branch-${notice.branch_id}`);
    } else if (notice.target_type === 'company' && !notice.company_id && !notice.branch_id) {
      // 전체 공지사항인 경우
      targets.push('all');
    }
    setSelectedTargets(targets);
    
    // 선택된 대상의 인원수 계산
    let totalCount = 0;
    targets.forEach(key => {
      const node = findNodeByKey(organizationTree, key);
      if (node) {
        totalCount += node.userCount || 0;
      }
    });
    setSelectedUserCount(totalCount);
    
    // 공지 작성 탭으로 전환
    setActiveTab('create');
  };

  // 즉시 발송
  const handleSendNow = async (notice: NoticeWithRelations) => {
    try {
      await NoticeService.sendNotice(notice.id);
      notification.success({
        message: '발송 완료',
        description: '공지사항이 발송되었습니다.',
        placement: 'topRight',
        duration: 4,
      });
      await loadNotices(currentUser);
    } catch (error) {
      logger.error('Error sending notice:', error);
      message.error('공지사항 발송 중 오류가 발생했습니다.');
    }
  };

  // 삭제
  const handleDelete = async (noticeId: string) => {
    try {
      await NoticeService.deleteNotice(noticeId);
      notification.success({
        message: '삭제 완료',
        description: '공지사항이 삭제되었습니다.',
        placement: 'topRight',
        duration: 3,
      });
      await loadNotices(currentUser);
    } catch (error) {
      logger.error('Error deleting notice:', error);
      message.error('공지사항 삭제 중 오류가 발생했습니다.');
    }
  };


  // 노드 찾기 헬퍼 함수
  const findNodeByKey = (nodes: OrganizationNode[], key: React.Key): OrganizationNode | null => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children) {
        const found = findNodeByKey(node.children as OrganizationNode[], key);
        if (found) return found;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  return (
    <div className="notice-management">
      <h1 className="page-title">공지사항 관리</h1>

      <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" destroyInactiveTabPane>
        <TabPane tab="발송 이력" key="history">
          <div style={{ marginBottom: 16 }}>
            <Space>
              <RangePicker 
                value={filterDateRange}
                onChange={(dates) => {
                  setFilterDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null);
                }}
              />
              <Select 
                value={filterStatus}
                onChange={setFilterStatus}
                style={{ width: 120 }}
              >
                <Option value="all">전체 상태</Option>
                <Option value="sent">발송완료</Option>
                <Option value="scheduled">예약</Option>
              </Select>
              <Input.Search 
                placeholder="제목 검색" 
                style={{ width: 300 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={() => loadNotices(currentUser, hasLoadedStats)}
              />
              <Button icon={<ReloadOutlined />} onClick={() => {
                loadNotices(currentUser, true);
                setHasLoadedStats(true);
              }}>
                새로고침
              </Button>
            </Space>
          </div>

          <Table
            columns={noticeColumns}
            dataSource={tableDataSource}
            rowKey="id"
            loading={tableLoading}
            pagination={{
              ...pagination,
              total: tableDataSource.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `전체 ${total}건`,
            }}
            onChange={(newPagination) => {
              setPagination({
                current: newPagination.current || 1,
                pageSize: newPagination.pageSize || 10,
              });
            }}
          />
        </TabPane>

        <TabPane tab="공지 작성" key="create">
          <Form form={form} layout="vertical" initialValues={{ sendType: 'immediate', noticeType: 'NOTICE' }}>
            <Row gutter={24}>
              <Col span={14}>
                <Card>
                  <Form.Item
                    label="구분"
                    name="noticeType"
                    rules={[{ required: true, message: '구분을 선택해주세요' }]}
                  >
                    <Radio.Group>
                      <Radio value="NOTICE">공지사항</Radio>
                      <Radio value="TBM">TBM</Radio>
                    </Radio.Group>
                  </Form.Item>
                  
                  <Form.Item
                    label="공지 제목"
                    name="title"
                    rules={[{ required: true, message: '제목을 입력해주세요' }]}
                  >
                    <Input placeholder="공지사항 제목을 입력하세요" size="large" />
                  </Form.Item>

                  <Form.Item
                    label="공지 내용"
                    name="content"
                    rules={[{ required: true, message: '내용을 입력해주세요' }]}
                  >
                    <TextArea
                      rows={12}
                      placeholder="공지사항 내용을 입력하세요"
                      showCount
                      maxLength={1000}
                    />
                  </Form.Item>

                </Card>
              </Col>

            <Col span={10}>
              <Card title="발송 대상 선택" extra={`선택된 대상: ${selectedUserCount}명`}>
                {treeLoading ? (
                  <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" tip="조직도를 불러오는 중..." />
                  </div>
                ) : (
                  <Tree
                    checkable
                    defaultExpandAll
                    onCheck={handleTreeSelect}
                    checkedKeys={selectedTargets}
                    treeData={organizationTree}
                    height={300}
                  />
                )}
              </Card>

              <Card title="발송 옵션" style={{ marginTop: 16 }}>
                <Form.Item name="sendType">
                  <Radio.Group>
                    <Space direction="vertical">
                      <Radio value="immediate">즉시 발송</Radio>
                      <Radio value="scheduled">예약 발송</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                
                <Form.Item 
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.sendType !== currentValues.sendType}
                >
                  {({ getFieldValue }) =>
                    getFieldValue('sendType') === 'scheduled' ? (
                      <Form.Item name="scheduledTime" rules={[{ required: true, message: '발송 시간을 선택해주세요' }]}>
                        <DatePicker
                          showTime
                          placeholder="발송 시간 선택"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>

                <Space style={{ marginTop: 16, width: '100%' }} direction="vertical">
                  <Button block icon={<EyeOutlined />} onClick={handlePreview}>
                    미리보기
                  </Button>
                  <Button
                    block
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                    loading={sendingNotice}
                    disabled={selectedTargets.length === 0}
                  >
                    {editingNotice ? '수정하기' : '발송하기'}
                  </Button>
                  {editingNotice && (
                    <Button
                      block
                      onClick={() => {
                        setEditingNotice(null);
                        form.resetFields();
                        setSelectedTargets([]);
                        setSelectedUserCount(0);
                      }}
                    >
                      취소
                    </Button>
                  )}
                </Space>
              </Card>
            </Col>
            </Row>
          </Form>
        </TabPane>
      </Tabs>

      {/* 미리보기 모달 */}
      <Modal
        title="공지사항 미리보기"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={500}
      >
        <div className="preview-content">
          <h2>{form.getFieldValue('title') || '제목 없음'}</h2>
          <Divider />
          <p style={{ whiteSpace: 'pre-wrap' }}>{form.getFieldValue('content') || '내용 없음'}</p>
        </div>
      </Modal>

      {/* 상세 모달 */}
      <Modal
        title="공지사항 상세"
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={null}
      >
        {selectedNotice && (
          <>
            <Row gutter={24}>
              <Col span={12}>
                <h3>공지 정보</h3>
                <p><strong>제목:</strong> {selectedNotice.title}</p>
                <p><strong>대상:</strong> {
                  selectedNotice.target_type === 'branch' && selectedNotice.branch
                    ? selectedNotice.branch.branch_name
                    : selectedNotice.target_type === 'company'
                    ? (!selectedNotice.company_id && !selectedNotice.branch_id
                      ? '전체'
                      : selectedNotice.company
                      ? `${selectedNotice.company.name} 전체`
                      : '-')
                    : '-'
                }</p>
                <p><strong>발송일시:</strong> {
                  selectedNotice.sent_at 
                    ? dayjs(selectedNotice.sent_at).format('YYYY-MM-DD HH:mm') 
                    : '미발송'
                }</p>
                <p><strong>내용:</strong></p>
                <div className="notice-content-box" style={{ 
                  padding: '12px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedNotice.content}
                </div>
              </Col>
              <Col span={12}>
                <h3>읽음 현황</h3>
                <Progress
                  percent={selectedNotice.read_rate || 0}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <p style={{ marginTop: 8 }}>
                  전체 {selectedNotice.total_recipients || 0}명 중 {selectedNotice.read_count || 0}명 확인
                </p>
                
                {selectedNotice.is_sent && (
                  <>
                    <p style={{ marginTop: 16 }}><strong>미확인자 명단</strong></p>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <List
                        size="small"
                        dataSource={selectedNotice.unreadUsers || []}
                        renderItem={(user) => (
                          <List.Item>
                            <List.Item.Meta
                              avatar={<Avatar icon={<TeamOutlined />} />}
                              title={user.userName}
                              description={`${user.branchName} | ${user.phoneNumber}`}
                            />
                          </List.Item>
                        )}
                        locale={{ emptyText: '모든 사용자가 확인했습니다.' }}
                      />
                    </div>
                  </>
                )}
              </Col>
            </Row>
          </>
        )}
      </Modal>
    </div>
  );
};

export default NoticeManagement;